import moment from "moment";
import { config } from "../../config";
import { QHelpers } from "../helpers";

export type StripeOAuthReturnTo = "setup" | "stripe";

/** JWT for Stripe OAuth state — prefers impersonated user token when admin switched account. */
export function getStripeAccessToken(): string | null {
    const switchToken = QHelpers.store.getImpersonationToken();
    if (switchToken) {
        return switchToken;
    }

    try {
        const stored = localStorage.getItem(config.LOCAL_STORE.ACCOUNT);
        if (!stored) {
            return null;
        }
        const account = JSON.parse(stored);
        return account?.access_token ?? null;
    } catch {
        return null;
    }
}

export function buildStripeOAuthUrl(
    accessToken: string,
    returnTo: StripeOAuthReturnTo = "setup"
): string {
    const stripeRedirectUri = process.env.REACT_APP_STRIPE_REDIRECT_URL || "";
    const state = accessToken
        + "__"
        + moment().format("YYYYMMDDHHmmss")
        + (returnTo === "stripe" ? "__stripe" : "");

    return (
        config.INTEGRATION.STRIPE.CONNECT_URL
        + "&state=" + encodeURIComponent(state)
        + "&redirect_uri=" + encodeURIComponent(stripeRedirectUri)
    );
}

/** Mask token for display — shows prefix (sk_live_/sk_test_) and last N chars. */
export function maskStripeAccessToken(token?: string | null, tailLength = 6): string {
    if (!token) {
        return "n/a";
    }

    const tail = token.slice(-tailLength);
    const prefixMatch = token.match(/^(sk_(?:live|test)_)/);
    const prefix = prefixMatch ? prefixMatch[1] : "";

    if (prefix) {
        return `${prefix}••••${tail}`;
    }

    if (token.length <= tailLength) {
        return `••••${tail}`;
    }

    return `••••••••${tail}`;
}

export function redirectToStripeOAuth(returnTo: StripeOAuthReturnTo = "setup"): boolean {
    const accessToken = getStripeAccessToken();
    if (!accessToken) {
        return false;
    }

    window.location.href = buildStripeOAuthUrl(accessToken, returnTo);
    return true;
}
